import { useState, useEffect, useCallback } from 'react';
import { buildTripsMonitoredData, buildTripStatusApiData } from '../mockData';
import * as service from '../usageReportingService';
import type { TimeframeSelection } from '../../../types/timeframe';
import type { TripsMonitoredUsageData } from '../../../types/tripsMonitoredUsage';
import type { TripStatusApiUsageData } from '../../../types/tripStatusApiUsage';

// Call the real BFF routes (/api/usage-reporting/*). Set to `true` to use
// mock/static data instead of calling the backend in the running app.
const USE_MOCK_DATA = false;

export function useUsageReportingData(timeframe: TimeframeSelection, fetchTrigger: number = 0) {
  const [tripsMonitoredData, setTripsMonitoredData] =
    useState<TripsMonitoredUsageData | null>(null);
  const [tripStatusData, setTripStatusData] =
    useState<TripStatusApiUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tripsMonitoredError, setTripsMonitoredError] = useState(false);
  const [tripStatusError, setTripStatusError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTripsMonitoredError(false);
    setTripStatusError(false);
    try {
      if (USE_MOCK_DATA) {
        // Mock data is derived from the selected timeframe so that every
        // dependent component (chart, tables, scorecards) re-renders to match
        // the calendar date range.
        setTripsMonitoredData(buildTripsMonitoredData(timeframe));
        setTripStatusData(buildTripStatusApiData(timeframe));
      } else {
        // Real API calls via the Trip Center BFF. Trip Status API is optional
        // (gated by the API_ACCESS feature / admin permission), so a failure
        // there degrades gracefully to null instead of failing the whole page.
        const [monitored, statusApi] = await Promise.all([
          service.getTripsMonitoredUsage(
            timeframe.startDate,
            timeframe.endDate,
            timeframe.granularity
          ).catch((err) => {
            setTripsMonitoredError(true);
            return null;
          }),
          service
            .getTripStatusApiUsage(
              timeframe.startDate,
              timeframe.endDate,
              timeframe.granularity
            )
            .catch(() => {
              setTripStatusError(true);
              return null;
            }),
        ]);
        setTripsMonitoredData(monitored);
        setTripStatusData(statusApi);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [timeframe.startDate, timeframe.endDate, timeframe.granularity, fetchTrigger]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { tripsMonitoredData, tripStatusData, loading, error, tripsMonitoredError, tripStatusError, refetch: fetchData };
}
